"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useRouter } from "next/navigation";
import { useContent } from "@/components/shell/ContentContext";
import { LOCALE_COOKIE, enabledLocales as enabledLocalesOf } from "./localized";
import { CATALOG, DEFAULT_LOCALE, resolveMessage, type MessageKey } from "./catalog";

const STORAGE_KEY = "northstar.locale";

/* The server renders translated content from this cookie, so a change of
   language sets it and asks the server for the page again. */
const readCookie = () =>
  document.cookie.split("; ").find((entry) => entry.startsWith(`${LOCALE_COOKIE}=`))?.slice(LOCALE_COOKIE.length + 1) ?? "";

const writeCookie = (code: string) => {
  document.cookie = `${LOCALE_COOKIE}=${code}; path=/; max-age=31536000; samesite=lax`;
};

type I18nValue = {
  locale: string;
  enabledLocales: string[];
  setLocale: (code: string) => void;
  t: (key: MessageKey) => string;
};

const I18nContext = createContext<I18nValue>({
  locale: DEFAULT_LOCALE,
  enabledLocales: [DEFAULT_LOCALE],
  setLocale: () => undefined,
  t: (key) => CATALOG.en[key],
});

export const useI18n = () => useContext(I18nContext);

function readStoredLocale(): string {
  try {
    return localStorage.getItem(STORAGE_KEY) ?? "";
  } catch {
    return "";
  }
}

/* The browser's preference list matched against what the operator enabled:
   the exact tag first ("pt-BR" lands on Português (BR) when it is on), then
   the language family ("pt-BR" lands on plain Português, and "sv-SE" on
   "sv"). No match means the site's default language. */
function detectLocale(enabled: string[], siteDefault: string): string {
  const preferred = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const tag of preferred) {
    if (!tag) continue;
    const exact = enabled.find((code) => code.toLowerCase() === tag.toLowerCase());
    if (exact) return exact;
    const family = tag.toLowerCase().split("-")[0];
    const related = enabled.find((code) => code.toLowerCase().split("-")[0] === family);
    if (related) return related;
  }
  return siteDefault;
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const content = useContent();
  const settings = content.i18n;
  const router = useRouter();

  const enabledLocales = useMemo(() => enabledLocalesOf(content), [content]);
  const siteDefault = enabledLocales[0] ?? DEFAULT_LOCALE;

  /* SSR and the first client render agree on English; the stored or detected
     locale applies after mount, so hydration never mismatches. */
  const [locale, setLocaleState] = useState<string>(DEFAULT_LOCALE);

  useEffect(() => {
    /* The cookie is what the server just rendered from: set by the selector,
       or by /api/locale for whoever drives the site from outside, like the
       factory preview showing a draft in Swedish. A usable cookie wins; the
       stored choice and the browser preference only fill in without one. */
    const cookie = readCookie();
    const stored = readStoredLocale();
    const usable = (value: string) => value !== "" && enabledLocales.includes(value);
    const resolved = usable(cookie) ? cookie : usable(stored) ? stored : detectLocale(enabledLocales, siteDefault);

    setLocaleState(resolved);

    try {
      localStorage.setItem(STORAGE_KEY, resolved);
    } catch {
      /* no storage */
    }

    if (cookie !== resolved) {
      writeCookie(resolved);
      router.refresh();
    }
  }, [enabledLocales, siteDefault, router]);

  const setLocale = useCallback(
    (code: string) => {
      if (!enabledLocales.includes(code)) return;
      setLocaleState(code);
      try {
        localStorage.setItem(STORAGE_KEY, code);
      } catch {
        /* Blocked storage only loses the preference across visits. */
      }
      writeCookie(code);
      router.refresh();
    },
    [enabledLocales, router],
  );

  const t = useCallback(
    (key: MessageKey): string => {
      const override = settings.overrides.find((o) => o.locale === locale && o.key === key);
      if (override && override.text) return override.text;
      return resolveMessage(locale, key);
    },
    [locale, settings.overrides],
  );

  const value = useMemo(() => ({ locale, enabledLocales, setLocale, t }), [locale, enabledLocales, setLocale, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
